import React, { useState, useEffect } from 'react';
import { Card, CardBody, Col, Form, FormGroup, Label, Input, Row } from 'reactstrap';
import Chart from 'react-google-charts';

const CustomerCoverage = () => {
  const [rsm, setRsm] = useState('');
  const [month, setMonth] = useState('');
  const [year, setYear] = useState('2025');
  const [data, setData] = useState<(string | number)[][]>([
    ['Month', 'Covered Customers', 'Unique Customers'],
  ]);

  useEffect(() => {
    const yearlyDataMap: Record<string, (string | number)[][]> = {
      '2025': [
        ['Month', 'All Customers', 'Unique Customers', 'Unique Customers (Previous Year)'],
        ['January', 200, 150, 140],
        ['February', 220, 160, 145],
        ['March', 250, 170, 150],
        ['April', 240, 165, 155],
        ['May', 260, 180, 160],
        ['June', 280, 190, 165],
        ['July', 270, 185, 160],
        ['August', 275, 188, 162],
        ['September', 290, 195, 170],
        ['October', 300, 200, 175],
        ['November', 310, 205, 180],
        ['December', 320, 210, 185],
      ],
      '2024': [
        ['Month', 'All Customers', 'Unique Customers', 'Unique Customers (Previous Year)'],
        ['January', 180, 140, 130],
        ['February', 200, 150, 135],
        ['March', 230, 160, 140],
        ['April', 220, 155, 145],
        ['May', 240, 170, 150],
        ['June', 260, 180, 155],
        ['July', 250, 175, 150],
        ['August', 255, 178, 152],
        ['September', 270, 185, 160],
        ['October', 280, 190, 165],
        ['November', 290, 195, 170],
        ['December', 300, 200, 175],
      ],
      '2023': [
        ['Month', 'All Customers', 'Unique Customers', 'Unique Customers (Previous Year)'],
        ['January', 160, 130, 120],
        ['February', 180, 140, 125],
        ['March', 210, 150, 130],
        ['April', 200, 145, 135],
        ['May', 220, 160, 140],
        ['June', 240, 170, 145],
        ['July', 230, 165, 140],
        ['August', 235, 168, 142],
        ['September', 250, 175, 150],
        ['October', 260, 180, 155],
        ['November', 270, 185, 160],
        ['December', 280, 190, 165],
      ],
    };

    const dailyDataMap: Record<string, (string | number)[][]> = {
      'January': Array.from({ length: 31 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'February': Array.from({ length: 28 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'March': Array.from({ length: 31 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'April': Array.from({ length: 30 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'May': Array.from({ length: 31 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'June': Array.from({ length: 30 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'July': Array.from({ length: 31 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'August': Array.from({ length: 31 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'September': Array.from({ length: 30 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'October': Array.from({ length: 31 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'November': Array.from({ length: 30 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
      'December': Array.from({ length: 31 }, (_, i) => [` ${i + 1}`, Math.random() * 100 + 150, Math.random() * 50 + 100]),
    };

    if (month) {
      const dailyData = dailyDataMap[month] || [];
      setData([['Date', 'All Customers', 'Unique Customers'], ...dailyData]);
    } else {
      const yearlyData = yearlyDataMap[year] || [['Month', 'All Customers', 'Unique Customers', 'Unique Customers (Previous Year)']];
      setData(yearlyData);
    }
  }, [year, month]);

  const options = {
    title: 'Customer Coverage',
    hAxis: { title: 'Date', format: 'string' }, // Updated to treat the first column as a label
    vAxis: { title: 'Number of Customers', minValue: 0 },
    legend: { position: 'top' },
    chartArea: { width: '90%' },
    series: {
      0: { color: '#4285F4', lineWidth: 3 }, // All customer coverage (current year)
      1: { color: '#34A853', lineWidth: 3 }, // Unique customer coverage (current year)
      2: { color: '#A9A9A9', lineWidth: 1, lineDashStyle: [4, 4] }, // Unique customer coverage (previous year, ghost line)
    },
  };

  return (
    <Col className="col-sm-12 col-xl-12 box-col-6">
      <Card>
        <CardBody>
          <Form className="mb-3">
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="rsm" className="mr-2">
                    RSM
                  </Label>
                  <Input
                    type="select"
                    id="rsm"
                    value={rsm}
                    onChange={(e) => setRsm(e.target.value)}
                  >
                    {/* <option value="">All RSMs</option> */}
                    <option value="RSM 1">RBM - KOLKATA</option>
                    <option value="RSM 2">RBM - MUMBAI</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="month" className="mr-2">
                    Month
                  </Label>
                  <Input
                    type="select"
                    id="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    <option value="">All Months</option>
                    <option value="January">January</option>
                    <option value="February">February</option>
                    <option value="March">March</option>
                    <option value="April">April</option>
                    <option value="May">May</option>
                    <option value="June">June</option>
                    <option value="July">July</option>
                    <option value="August">August</option>
                    <option value="September">September</option>
                    <option value="October">October</option>
                    <option value="November">November</option>
                    <option value="December">December</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="year" className="mr-2">
                    Year
                  </Label>
                  <Input
                    type="select"
                    id="year"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    <option value="2025">2025</option>
                    <option value="2024">2024</option>
                    <option value="2023">2023</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>
          </Form>
          <Chart
            width={'100%'}
            height={'400px'}
            chartType="LineChart"
            loader={<div>Loading Chart...</div>}
            data={data}
            options={options}
          />
        </CardBody>
      </Card>
    </Col>
  );
};

export default CustomerCoverage;